import type { SalesBreakdown, SalesQuery, SalesRow } from "@khazzin/api-client";
import type { ChartRow } from "../charts";
import { addDays, type DateRange } from "../patterns/dateRange";

/**
 * The Sales & Revenue dashboard's arithmetic (EP13-S10), apart from the screen
 * so it is tested without rendering. The server sums and divides; this only
 * lays its rows out: per currency, day by day, and beside the previous period.
 *
 * Shared by the Admin Console and the Store Portal (EP10-S13).
 */

/** The channels the filter offers, in the order it lists them. */
export const SALES_CHANNELS = ["shopify", "woocommerce", "manual", "api", "csv"] as const;

/** The dimension an order with no province, city or SKU is under. */
export const UNASSIGNED = "unassigned";

/** Every day of a range, both ends included. */
export function datesOf(range: DateRange): string[] {
	const out: string[] = [];
	for (let day = range.from; day <= range.to; day = addDays(day, 1)) {
		out.push(day);
	}
	return out;
}

/** A dashboard's range and dimension filters, as the admin bar reads them. */
export interface SalesFilters {
	range: DateRange;
	dims: Partial<Record<string, string>>;
}

/** The bar's filters as the sales endpoint reads them. */
export function salesQueryOf(filters: SalesFilters): Omit<SalesQuery, "signal"> {
	const query: Omit<SalesQuery, "signal"> = { ...filters.range };
	for (const key of ["storeId", "channel", "provinceId", "cityId"] as const) {
		const value = filters.dims[key];
		if (value !== undefined) {
			query[key] = value;
		}
	}
	return query;
}

export interface SalesTile {
	/** Null when the period sold nothing at all. */
	currency: string | null;
	revenue: number;
	orders: number;
	units: number;
	aov: number | null;
	previous: { revenue: number; orders: number; units: number; aov: number | null } | null;
}

/**
 * One set of four tiles per currency. A period with no sales still shows one
 * set, at zero with no AOV (AC2), rather than an empty row.
 */
export function tilesOf(summary: SalesBreakdown | undefined): SalesTile[] {
	const rows = summary?.rows ?? [];
	if (rows.length === 0) {
		return [{ currency: null, revenue: 0, orders: 0, units: 0, aov: null, previous: null }];
	}
	return rows.map((row) => {
		const before = summary?.previous.find((p) => p.currency === row.currency);
		return {
			currency: row.currency,
			revenue: row.revenueMinor,
			orders: row.orders,
			units: row.units,
			aov: row.aovMinor,
			previous:
				before === undefined
					? null
					: {
							revenue: before.revenueMinor,
							orders: before.orders,
							units: before.units,
							aov: before.aovMinor,
						},
		};
	});
}

/** The change from before to now in percent, one decimal; none over nothing. */
export function deltaPct(now: number | null, before: number | null | undefined): number | null {
	if (now === null || before === null || before === undefined || before === 0) {
		return null;
	}
	return Math.round(((now - before) / before) * 1000) / 10;
}

export type SalesField = "revenueMinor" | "orders" | "units";

function byDay(rows: SalesRow[], currency: string | null, field: SalesField): Map<string, number> {
	const out = new Map<string, number>();
	for (const row of rows) {
		if (row.date !== null && row.currency === currency) {
			out.set(row.date, (out.get(row.date) ?? 0) + row[field]);
		}
	}
	return out;
}

/**
 * The trend's rows: one per day of the range, zero on a day with no sales, and
 * the previous period's day N beside this range's day N when asked.
 */
export function trendRows(
	range: DateRange,
	trend: SalesBreakdown | undefined,
	currency: string | null,
	field: SalesField,
	withPrevious: boolean,
): ChartRow[] {
	const now = byDay(trend?.rows ?? [], currency, field);
	const before = byDay(trend?.previous ?? [], currency, field);
	const earlier =
		trend?.previousFrom && trend.previousTo
			? datesOf({ from: trend.previousFrom, to: trend.previousTo })
			: [];
	return datesOf(range).map((date, index) => {
		const row: ChartRow = { date, current: now.get(date) ?? 0 };
		if (withPrevious) {
			const day = earlier[index];
			row.previous = day === undefined ? null : (before.get(day) ?? 0);
		}
		return row;
	});
}

/** A tile's sparkline: its field per day of the range. */
export function sparkOf(
	range: DateRange,
	trend: SalesBreakdown | undefined,
	currency: string | null,
	field: SalesField,
): number[] {
	return trendRows(range, trend, currency, field, false).map((row) => Number(row.current));
}

/** Which dimension a grouped block's rows are keyed by. */
export type SalesDim = "channel" | "provinceId" | "cityId" | "storeId";

/**
 * A grouped block as bar rows for one currency: `key`, `current` revenue and,
 * when asked, `previous`; the biggest first.
 */
export function barRows(
	block: SalesBreakdown | undefined,
	dim: SalesDim,
	currency: string,
	withPrevious: boolean,
): ChartRow[] {
	const before = new Map<string, number>();
	for (const row of block?.previous ?? []) {
		if (row.currency === currency) {
			before.set(row[dim] ?? UNASSIGNED, row.revenueMinor);
		}
	}
	return (block?.rows ?? [])
		.filter((row) => row.currency === currency)
		.sort((a, b) => b.revenueMinor - a.revenueMinor)
		.map((row) => {
			const key = row[dim] ?? UNASSIGNED;
			const out: ChartRow = { key, current: row.revenueMinor };
			if (withPrevious) {
				out.previous = before.get(key) ?? 0;
			}
			return out;
		});
}

/** The currencies a block has rows in, in the order the server sent them. */
export function currenciesIn(block: SalesBreakdown | undefined): string[] {
	return [
		...new Set(
			(block?.rows ?? [])
				.map((row) => row.currency)
				.filter((currency): currency is string => currency !== null),
		),
	];
}

/** A top store's revenue change against the previous period. */
export function storeDelta(block: SalesBreakdown | undefined, row: SalesRow): number | null {
	const before = block?.previous.find(
		(p) => p.storeId === row.storeId && p.currency === row.currency,
	);
	return deltaPct(row.revenueMinor, before?.revenueMinor ?? null);
}
